import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';
import { useEntitlements } from '../hooks/useEntitlements';

/**
 * Multi-Player Video Wall — multiple identical Android players act as ONE big
 * screen by clock-syncing playback. Backend: VideoWallController.
 *
 *   GET    /video-walls                       → { walls }
 *   POST   /video-walls                       create wall (+ optional screens)
 *   GET    /video-walls/{id}                  → { wall } (+ screens, playlists)
 *   PUT    /video-walls/{id}                  update grid config
 *   DELETE /video-walls/{id}
 *   POST   /video-walls/{id}/assign-devices   bulk assign devices to cells
 *   POST   /video-walls/{id}/calibrate        each screen shows its grid number
 *   POST   /video-walls/{id}/playlists        create playlist (+ items)
 *   PUT    /video-walls/{id}/playlists/{pid}  replace name/active/items
 *   DELETE /video-walls/{id}/playlists/{pid}
 *   POST   /video-walls/{id}/start            start synced playback
 *   POST   /video-walls/{id}/stop             stop playback
 *
 * Backend wraps responses in a Laravel resource ({ data: { status, ... } });
 * hooks unwrap with `res?.data ?? res`.
 */

const LIST_KEY = 'videoWalls';
const wallKey = (id) => ['videoWall', id];

// Not asked for a package that explicitly lacks video_wall (the backend would 402): this also keeps
// the Locations page (a `locations` feature) from calling /video-walls on such a package.
export const useGetVideoWalls = () => {
  const { has } = useEntitlements();
  return useQuery(
    LIST_KEY,
    async () => {
      const res = (await axiosInstance.get('/video-walls')).data;
      return (res?.data ?? res)?.walls ?? [];
    },
    { staleTime: 15_000, enabled: has('video_wall') }
  );
};

export const useGetVideoWall = (id, enabled = true, refetchInterval = false) =>
  useQuery(
    wallKey(id),
    async () => {
      const res = (await axiosInstance.get(`/video-walls/${id}`)).data;
      return (res?.data ?? res)?.wall ?? null;
    },
    { enabled: !!id && enabled, staleTime: 5_000, refetchInterval }
  );

export const useCreateVideoWall = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/video-walls', payload), {
    onSuccess: (res) => {
      qc.invalidateQueries(LIST_KEY);
      const body = res?.data?.data ?? res?.data;
      if (body?.status === 409) showToast('warn', 'Video Wall', body?.msg); // device already on another wall
      else showToast('success', 'Video Wall', 'Wall created.');
    },
    onError: (err) => showToast('error', 'Video Wall', err.response?.data?.message),
  });
};

/** device_id → { wall_id, wall_name } for devices already on a wall (Device→Wall 1:1 badge). */
export const useDeviceUsage = () =>
  useQuery('wallDeviceUsage', async () => {
    const res = (await axiosInstance.get('/video-walls/device-usage')).data;
    return (res?.data ?? res)?.usage ?? {};
  }, { staleTime: 15_000 });

export const useUpdateVideoWall = (id) => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.put(`/video-walls/${id}`, payload), {
    onSuccess: () => {
      qc.invalidateQueries(wallKey(id));
      qc.invalidateQueries(LIST_KEY);
      showToast('success', 'Video Wall', 'Wall updated.');
    },
    onError: (err) => showToast('error', 'Video Wall', err.response?.data?.message),
  });
};

export const useDeleteVideoWall = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.delete(`/video-walls/${id}`), {
    onSuccess: () => {
      qc.invalidateQueries(LIST_KEY);
      showToast('success', 'Video Wall', 'Wall deleted.');
    },
    onError: (err) => showToast('error', 'Video Wall', err.response?.data?.message),
  });
};

export const useAssignDevices = (id) => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post(`/video-walls/${id}/assign-devices`, payload), {
    onSuccess: (res) => {
      qc.invalidateQueries(wallKey(id));
      const body = res?.data?.data ?? res?.data;
      if (body?.status === 409) showToast('warn', 'Video Wall', body?.msg);
      else showToast('success', 'Video Wall', 'Devices assigned.');
    },
    onError: (err) => showToast('error', 'Video Wall', err.response?.data?.message),
  });
};

export const useCalibrateWall = (id) => {
  const { showToast } = useCustomToast();
  return useMutation(() => axiosInstance.post(`/video-walls/${id}/calibrate`), {
    onSuccess: () => showToast('success', 'Video Wall', 'Each screen is showing its number.'),
    onError: (err) => showToast('error', 'Video Wall', err.response?.data?.message),
  });
};

export const useCreatePlaylist = (id) => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post(`/video-walls/${id}/playlists`, payload), {
    onSuccess: () => {
      qc.invalidateQueries(wallKey(id));
      showToast('success', 'Video Wall', 'Playlist saved.');
    },
    onError: (err) => showToast('error', 'Video Wall', err.response?.data?.message),
  });
};

export const useUpdatePlaylist = (id) => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(({ playlistId, ...payload }) =>
    axiosInstance.put(`/video-walls/${id}/playlists/${playlistId}`, payload), {
    onSuccess: () => {
      qc.invalidateQueries(wallKey(id));
      showToast('success', 'Video Wall', 'Playlist updated.');
    },
    onError: (err) => showToast('error', 'Video Wall', err.response?.data?.message),
  });
};

export const useDeletePlaylist = (id) => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((playlistId) =>
    axiosInstance.delete(`/video-walls/${id}/playlists/${playlistId}`), {
    onSuccess: () => {
      qc.invalidateQueries(wallKey(id));
      showToast('success', 'Video Wall', 'Playlist deleted.');
    },
    onError: (err) => showToast('error', 'Video Wall', err.response?.data?.message),
  });
};

export const useStartWall = (id) => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload = {}) => axiosInstance.post(`/video-walls/${id}/start`, payload), {
    onSuccess: (res) => {
      qc.invalidateQueries(wallKey(id));
      const body = res?.data?.data ?? res?.data;
      if (body?.status === 422) {
        showToast('warn', 'Video Wall', body?.msg || 'Add content first.');
      } else {
        showToast('success', 'Video Wall', 'Playback started — screens are syncing.');
      }
    },
    onError: (err) => showToast('error', 'Video Wall', err.response?.data?.message),
  });
};

/**
 * POST /video-walls/{id}/process-splits — pre-split full-wall video items into
 * per-cell pieces (Fix #2) so each device decodes a native-res slice. Runs the
 * ffmpeg work server-side (inline); returns per-item split_status.
 */
export const useProcessSplits = (id) => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  // Returns immediately (202) — the backend queues the ffmpeg split. The page
  // then polls split_status via useGetVideoWall's refetchInterval.
  return useMutation((payload = {}) => axiosInstance.post(`/video-walls/${id}/process-splits`, payload), {
    onSuccess: (res) => {
      qc.invalidateQueries(wallKey(id));
      const body = res?.data?.data ?? res?.data;
      showToast('info', 'Video Wall', body?.msg || 'Optimizing video for the wall…');
    },
    onError: (err) => showToast('error', 'Video Wall', err.response?.data?.message),
  });
};

/** GET /folder/media filtered to VIDEO + IMAGE — backs the wall content picker. */
export const useGetWallMediaLibrary = (enabled = true) =>
  useQuery(
    'videoWallMediaLibrary',
    async () => (await axiosInstance.get('/folder/media', {
      params: { section: 'MEDIA', type: 'VIDEO,IMAGE', per_page: 200, page: 1 },
    })).data,
    { enabled, staleTime: 30_000 }
  );

export const useStopWall = (id) => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(() => axiosInstance.post(`/video-walls/${id}/stop`), {
    onSuccess: () => {
      qc.invalidateQueries(wallKey(id));
      showToast('info', 'Video Wall', 'Playback stopped.');
    },
    onError: (err) => showToast('error', 'Video Wall', err.response?.data?.message),
  });
};

/**
 * POST /video-walls/{id}/resync — force a re-buffer + coordinated restart of the
 * active playlist (the "Re-sync All" button). Players re-run the pre-buffer +
 * ready barrier before starting together again.
 */
export const useResyncWall = (id) => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(() => axiosInstance.post(`/video-walls/${id}/resync`), {
    onSuccess: (res) => {
      qc.invalidateQueries(wallKey(id));
      const body = res?.data?.data ?? res?.data;
      if (body?.status === 422) {
        showToast('warn', 'Video Wall', body?.msg || 'Add content first.');
      } else {
        showToast('success', 'Video Wall', 'Re-syncing — screens are re-buffering.');
      }
    },
    onError: (err) => showToast('error', 'Video Wall', err.response?.data?.message),
  });
};
